import { Button, FormControl } from '@mui/material';
import { toastError, toastSuccess } from '@onestep/coreui/Toast';
import classNames from 'classnames';
import { find, isEmpty, map } from 'lodash';
import React, { useContext, useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useParams } from 'react-router';

import CustomDatePicker from '../../../../components/CustomDatePicker';
import Select from '../../../../components/Inputs/Select';
import TextField from '../../../../components/Inputs/TextField';
import useMixpanel from '../../../../hooks/useMixpanel';
import useClinicalRecordService from '../../../../services/clinicalRecordService';
import type { IClinicalHistory } from '../../../../services/clinicalRecordService/types';
import { formatTime, getDate } from '../../../../services/timeService';
import { dateTimeFormat } from '../../../../services/timeService/types';
import { serverTime } from '../../../../utils/serverTime';
import { eventModalContext } from '../index';
import type { EventsSchemaKeys } from './eventsSchema';
import { eventsSchema } from './eventsSchema';
import { eventType } from './index';
import styles from './index.module.scss';

interface IClinicalHistoryFormData extends IClinicalHistory {
  tagsToRemove?: string[];
  tagsToAppend?: string[];
}
interface IEventValue {
  type?: string;
  label?: string;
  required?: boolean;
  options?: {
    label: string;
    value: number | string;
  }[];
  isHidden?: boolean;
}

export default function EventForm() {
  const { patientUuid = '' } = useParams<{ patientUuid: string }>();
  const { eventUuid, setEventUuid, eventTab } = useContext(eventModalContext);
  const { trackClick } = useMixpanel();

  const newItem = eventUuid === 'none';
  const { record, postClinicalHistory, updateClinicalHistory } =
    useClinicalRecordService(patientUuid);
  const clinicalHistory =
    find(record?.clinicalHistory || [], ['uuid', eventUuid]) ?? ({} as IClinicalHistory);

  const {
    handleSubmit,
    register,
    reset,
    setError,
    formState: { errors, isSubmitting },
    control,
  } = useForm({
    ...(!isEmpty(clinicalHistory) && {
      defaultValues: {
        ...clinicalHistory,
        eventTimestamp: formatTime(
          clinicalHistory.eventTimestamp,
          dateTimeFormat.SERVER_DATE_FORMAT,
        ),
      },
      shouldUnregister: true,
    }),
  });

  const onSubmit = async (form: IClinicalHistoryFormData) => {
    if (eventTab === eventType.fall && getDate().isBefore(getDate(form.eventTimestamp))) {
      setError('eventTimestamp', {
        message: 'Event time cannot be in the future',
        type: 'manual',
      });
      return;
    }
    // eslint-disable-line
    if (form.eventTimestamp) {
      form.eventTimestamp = serverTime(form.eventTimestamp);
    }
    if (!form.tags && eventTab && eventTab !== eventType.other) {
      form.tags = [eventTab];
    }
    if (!form.therapistComment) {
      form.therapistComment = null;
    }
    form.tags = form.tags ?? [];
    try {
      if (newItem) {
        await postClinicalHistory(form);
        trackClick({
          action: 'add_event',
        });
      } else {
        form.tagsToRemove = [];
        form.tagsToAppend = form.tags as string[];
        await updateClinicalHistory({
          data: form,
          uuid: clinicalHistory.uuid,
        });
      }
      toastSuccess({ content: `Event ${newItem ? 'created' : 'edited'} successfully!` });
    } catch {
      toastError({ content: `Failed to ${newItem ? 'create' : 'edit'} event!` });
    } finally {
      setEventUuid();
    }
  };
  useEffect(() => {
    reset();
  }, [eventTab]);

  return (
    <form className="flex flex-col px-4" onSubmit={handleSubmit(onSubmit)}>
      {map(
        eventsSchema[eventTab as EventsSchemaKeys].properties,
        (
          value: IEventValue,
          key: 'uuid' | 'affectedSide' | 'eventTimestamp' | 'tags' | 'therapistComment' | 'title',
        ) => {
          return (
            <div key={key}>
              <div className="flex flex-col">
                {value.label && key !== 'eventTimestamp' ? (
                  <span className={styles.fieldLabel}>
                    {value.label}
                    {value.required ? <span className={styles.required}>*</span> : null}
                  </span>
                ) : null}
                {value.type === 'select' ? (
                  <Controller
                    render={({ field }) => (
                      <Select
                        variant="standard"
                        options={value.options ?? []}
                        className={classNames(styles.input, 'mb-2')}
                        disableUnderline
                        {...field}
                      />
                    )}
                    name={key}
                    control={control}
                    rules={{
                      required: {
                        value: value.required ?? false,
                        message: 'This field is required',
                      },
                    }}
                  />
                ) : key !== 'eventTimestamp' ? (
                  <TextField
                    type={value.type}
                    {...register(key, {
                      required: {
                        value: value.required ?? false,
                        message: 'This field is required',
                      },
                    })}
                    {...value}
                    label={''}
                    error={!!errors[key]}
                    helperText={errors[key]?.message}
                    fullWidth
                    wrapperClassName="mb-2"
                  />
                ) : (
                  <FormControl
                    error={!!errors[key]}
                    required
                    className={classNames({
                      [styles.error]: errors[key],
                    })}
                  >
                    <Controller
                      render={({ field }) => (
                        <CustomDatePicker
                          label={'Event Time'}
                          {...register(key, {
                            required: 'This field is required',
                          })}
                          isRequired
                          onChange={(date) => {
                            field.onChange(date?.format(dateTimeFormat.SERVER_DATE_FORMAT));
                          }}
                          defaultValue={
                            clinicalHistory.eventTimestamp
                              ? getDate(clinicalHistory.eventTimestamp)
                              : null
                          }
                          isError={!!errors[key]}
                          helperText={errors[key]?.message as string}
                          useBlackBorder
                          labelClassName={styles.datePicker}
                        />
                      )}
                      name={key}
                      control={control}
                    />
                  </FormControl>
                )}
              </div>
            </div>
          );
        },
      )}
      {!isEmpty(errors) ? (
        <div className={classNames('color-error', styles.error)}>Please enter required fields</div>
      ) : null}
      <div className={classNames('flex justify-center', styles.btnContainer)}>
        <Button type="submit" color="primary" variant="contained" classes={{ root: styles.addBtn }}>
          {isSubmitting ? 'Loading ...' : newItem ? 'Add' : 'Save'}
        </Button>
      </div>
    </form>
  );
}
